// @flow
import * as React from 'react';
import { useClient } from '../../hooks/client-context';
import {
  Portal,
  useSetRecordedLineAgreementCheckMutation,
  useSetSoaAgreementCheckMutation,
  useSetTpmoAgreementCheckMutation
} from '../../../../shared';
import { enqueueSnackbar } from 'notistack';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useConfirm } from '@coverright/features';
import { theme } from '../../../../app/theme';
import { useMaPlansCount } from '@coverright/data-access/medicare';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';

export const Soa = () => {
  const client = useClient();
  const { totalOrgCount, totalPlansCount, loading } = useMaPlansCount(client?.zip, {
    client: mainBackendApolloClient
  });
  const [saveSoaAgreement, { loading: soaLoading }] = useSetSoaAgreementCheckMutation({
    variables: { clientId: client?.id },
    onCompleted: () => enqueueSnackbar('Saved', { variant: 'success' })
  });
  const [saveTpmoAgreement, { loading: tpmoLoading }] = useSetTpmoAgreementCheckMutation({
    variables: { clientId: client?.id },
    onCompleted: () => enqueueSnackbar('Saved', { variant: 'success' })
  });
  const [saveRecordedLineAgreement, { loading: recordedLineLoading }] = useSetRecordedLineAgreementCheckMutation({
    variables: { clientId: client?.id },
    onCompleted: () => enqueueSnackbar('Saved', { variant: 'success' })
  });

  return (
    <Stack gap={3}>
      <Typography variant={'subtitle1'}>
        Scope of appointment
      </Typography>
      <Portal p={'12px 16px 12px 8px'}>
        <TPMOCheckbox checked={!!client?.recordedLineAgreementCheck} loading={loading || recordedLineLoading}
                      saveFn={saveRecordedLineAgreement} content={(
          <Typography fontWeight={500} variant={'body2'} color={'text.primary'}>Recorded Line agreement</Typography>
        )} />
      </Portal>

      <Portal p={'12px 16px 12px 8px'}>
        <TPMOCheckbox checked={!!client?.tpmoAgreementCheck} loading={loading || tpmoLoading} saveFn={saveTpmoAgreement}
                      content={(
                        <Stack mt={1}>
                          <Typography fontWeight={500} variant={'body2'} color={'text.primary'}>TPMO
                            Agreement:</Typography>
                          <Typography variant={'body2'} mt={.2} color={'text.secondary'}>
                            We do not offer every plan available in your area. Currently we
                            represent {totalOrgCount} organizations which
                            offer {totalPlansCount} products
                            in your area. Please contact http://medicare.gov/ , 1–800–MEDICARE, or your local State
                            Health Insurance
                            Program
                            (SHIP) to get information on all of your options.
                          </Typography>
                        </Stack>
                      )} />
      </Portal>

      <Portal p={'12px 16px 12px 8px'}>
        <TPMOCheckbox checked={!!client?.soaAgreementCheck} loading={loading || soaLoading} saveFn={saveSoaAgreement}
                      content={(
                        <Stack mt={1}>
                          <Typography fontWeight={500} variant={'body2'} color={'text.primary'}>SOA
                            Agreement:</Typography>
                          <Typography variant={'body2'} mt={.2} color={'text.secondary'}>
                            I work for CoverRight Insurance Services Inc., and in your area, we have a wide variety of
                            plans such as:<br /><br />

                            Medicare Advantage plans, Medicare Advantage Prescription Drug plans, Stand-alone
                            Prescription Drug plans, Medicare Supplement Insurance Plans, Optional Supplemental
                            Benefits, Hospital Indemnity Plans<br /><br />

                            Would you like to discuss all of these options or are you only interested in certain ones?
                            (CSR/Agent must wait for an affirmative response and proceed accordingly based on
                            beneficiary's choice).<br /><br />

                            I can give you a brief overview of each of these plans, then you can decide which plan might
                            be best for you based on your needs. Would that be ok? (Agent to wait for response).<br /><br />

                            This conversation has no effect on your current or future health coverage unless you enroll
                            in a plan today. Talking to me does not obligate you to enroll or automatically enroll you
                            in a plan. (An affirmative response is required).
                          </Typography>
                        </Stack>
                      )} />
      </Portal>
    </Stack>
  );
};

const TPMOCheckbox = ({ content, checked, saveFn, loading }: {
  content: React.ReactNode,
  checked: boolean,
  loading: boolean,
  saveFn: () => Promise<any>
}) => {
  const [value, setValue] = useState(checked);
  const confirm = useConfirm(theme);

  const handleChange = () => {
    confirm({
      content: 'Did you get verbal agreement on the SOA?',
      okButtonTitle: 'Yes',
      cancelButtonTitle: 'No'
    })
      .then(val => {
        if (val) {
          setValue(true);
          saveFn().catch(() => setValue(false));
        }
      });
  };

  useEffect(() => {
    setValue(checked);
  }, [checked]);

  return <Stack direction={'row'} spacing={2}>
    <FormControlLabel control={<Checkbox checked={value}
                                         sx={{ alignSelf: 'flex-start' }}
                                         size={'small'}
                                         disabled={loading || value}
                                         onChange={handleChange} />}
                      label={content} />
  </Stack>;
};
